import React, { useEffect, useState } from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import CustomInputField from '../../Common_Components/CustomInputField';
import { useNavigate, useParams } from 'react-router-dom';
import SaveButton from '../../Common_Components/SaveButton';
import ResetButton from '../../Common_Components/ResetButton';
import axiosInstance from '../../axios/axios';
import { Toast } from '../../Common_Components/Toast';
import CustomSelectBox from '../../Common_Components/CustomSelectBox';


const AddClient = () => {
    const navigate = useNavigate();
    const { id } = useParams(); // To get the client ID from the URL if editing an existing client
    const [clientData, setClientData] = useState({
        CD_Name: '',
        CD_Address: '',
        CD_City: '',
        CD_Zipcode: '',
        CD_POC: '',
        CD_Phone: '',
        CD_Email: '',
        CD_BillingAddress: '',
        CD_Billing_City: '',
        CD_Billing_Zipcode: ''
    });
    const [countries, setCountries] = useState([]);
    const [country, setCountry] = useState(null);
    const [billingcountry, setBillingCountry] = useState(null);
    const [states, setStates] = useState([]);
    const [state, setState] = useState(null);
    const [billingState, setBillingstate] = useState(null);
    const statusList = [{ id: 1, label: "Active" }, { id: 0, label: "Inactive" }];
    const [status, setStatus] = useState(null);

    const handleBackClick = () => navigate(-1);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setClientData((prevData) => ({ ...prevData, [name]: value }));
    };

    useEffect(() => {
        // Fetch countries from API
        const fetchCountries = async () => {
            try {
                const response = await axiosInstance.get('/common/countries');
                const { success, countries } = response.data;
                if (success) {
                    const vals = countries.map((val) => {
                        return { id: val.CY_ID, label: val.CY_Name };
                    });
                    setCountries(vals);
                } else {
                    setCountries([]);
                }
            } catch (error) {
                console.error('Error fetching countries:', error);
                Toast.error('Failed to fetch countries.');
            }
        };
        fetchCountries();
    }, []);

    useEffect(() => {
        // Fetch states when country is selected
        const fetchStates = async () => {
            try {
                const response = await axiosInstance.get(`/common/states/${country?.id}`);
                const { success, states } = response.data;
                if (success) {
                    const vals = states.map((val) => {
                        return { id: val.ST_ID, label: val.ST_Name };
                    });
                    setStates(vals);
                } else {
                    setStates([]);
                }
            } catch (error) {
                console.error('Error fetching states:', error);
                Toast.error('Failed to fetch states.');
            }
        };
        if (country?.id) {
            fetchStates();
        }
    }, [country?.id]);

    useEffect(() => {
        // Fetch client data if editing
        const fetchClientData = async () => {
            if (id) {
                try {
                    const response = await axiosInstance.get(`/clients/view/${id}`);
                    const { success, client } = response.data;
                    if (success) {
                        setClientData({
                            CD_Name: client.CD_Name,
                            CD_Address: client.CD_Address,
                            CD_City: client.CD_City,
                            CD_Zipcode: client.CD_Zipcode,
                            CD_POC: client.CD_POC,
                            CD_Phone: client.CD_Phone,
                            CD_Email: client.CD_Email,
                            CD_BillingAddress: client.CD_BillingAddress,
                            CD_Billing_City: client.CD_Billing_City,
                            CD_Billing_Zipcode: client.CD_Billing_Zipcode
                        });
                        setCountry(client.CD_Country ? { id: client.CD_Country, label: client.country_name } : null);
                        setState(client.CD_State ? { id: client.CD_State, label: client.city_name } : null);
                        setBillingCountry(client.CD_Billing_Country ? { id: client.CD_Billing_Country, label: client.billing_country_name } : null);
                        setBillingstate(client.CD_Billing_State ? { id: client.CD_Billing_State, label: client.billing_city_name } : null);
                        setStatus({ id: client.CD_Status, label: Number(client.CD_Status) === 1 ? 'Active' : 'Inactive' });
                    } else {
                        Toast.warning('Client data not found.');
                    }
                } catch (error) {
                    console.error('Error fetching client data:', error);
                    Toast.error('Failed to fetch client data.');
                }
            }
        };
        fetchClientData();
    }, [id]);

    const handleValidation = () => {
        // Validation checks for each field
        if (!clientData.CD_Name) {
            Toast.warning('Client Name is required');
            return false;
        }
        if (!clientData.CD_Address) {
            Toast.warning('Address is required');
            return false;
        }
        if (!country) {
            Toast.warning('Country is required');
            return false;
        }
        if (!state) {
            Toast.warning('State is required');
            return false;
        }
        if (!clientData.CD_Email) {
            Toast.warning('Email is required');
            return false;
        }
        // Email format validation
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(clientData.CD_Email)) {
            Toast.warning('Please enter a valid email address');
            return false;
        }

        if (!clientData.CD_POC) {
            Toast.warning('POC is required');
            return false;
        }
        if (!clientData.CD_BillingAddress) {
            Toast.warning('Billing Address is required');
            return false;
        }
        if (!status) {
            Toast.warning('Status is required');
            return false;
        }

        return true; // Return true if no errors
    };
    const handleSave = async (e) => {
        e.preventDefault();
        if (!handleValidation()) return;
        const request = {
            CD_Name: clientData.CD_Name,
            CD_Address: clientData.CD_Address,
            CD_Country: country?.id,
            CD_State: state?.id,
            CD_City: clientData.CD_City,
            CD_Zipcode: clientData.CD_Zipcode,
            CD_POC: clientData.CD_POC,
            CD_Phone: clientData.CD_Phone,
            CD_Email: clientData.CD_Email,
            CD_BillingAddress: clientData.CD_BillingAddress,
            CD_Billing_Country: billingcountry?.id,
            CD_Billing_State: billingState?.id,
            CD_Billing_City: clientData.CD_Billing_City,
            CD_Billing_Zipcode: clientData.CD_Billing_Zipcode,
            CD_Status: status?.id,
        };
        try {
            const response = id
                ? await axiosInstance.post(`/clients/edit/${id}`, request) // Update request for edit
                : await axiosInstance.post('/clients/add', request); // Add request for new client
            const { success, message } = response.data;
            if (success) {
                Toast.success(message);
                navigate('/client-management');
            } else {
                Toast.warning(message);
            }
        } catch (error) {
            Toast.error('Something went wrong!!!');
            console.error(error);
        }
    };
    //for reset form fields
    const handleReset = () => {
        setClientData({
            CD_Name: '',
            CD_Address: '',
            CD_Country: '',
            CD_State: '',
            CD_City: '',
            CD_Zipcode: '',
            CD_POC: '',
            CD_Phone: '',
            CD_Email: '',
            CD_BillingAddress: '',
            CD_Billing_Zipcode: '',
            CD_Billing_City: '',
            CD_Status: ''
        });
        setStatus(null)
        setCountry(null)
        setBillingCountry(null)
        setState(null)
        setBillingstate(null)
    };

    return (
        <Box display="flex" flexDirection="column" padding={{ xs: 1, sm: 2, md: 3 }} minHeight="100vh">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box display="flex" alignItems="center">
                    <ArrowBack onClick={handleBackClick} style={{
                        cursor: 'pointer',
                        marginRight: '8px',
                        marginBottom: '5px',
                        height: '34px',
                        width: '38px',
                        color: '#545454'
                    }} />
                    <Typography
                        gutterBottom
                        sx={{
                            fontFamily: 'Times New Roman',
                            fontWeight: '700',
                            fontSize: { xs: '24px', sm: '28px', md: '32px' },
                            lineHeight: '1.2',
                            color: '#545454'
                        }}
                    >
                        Add Client
                    </Typography>
                </Box>
                <Box display="flex" gap={2}>
                    <ResetButton label="Reset" onClick={handleReset} />
                    <SaveButton label="Save" onClick={handleSave} />
                </Box>
            </Box>

            <Paper elevation={3} sx={{ padding: { xs: 2, sm: 3, md: 4 } }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <CustomInputField
                            label="Client Name"
                            name="CD_Name"
                            value={clientData.CD_Name}
                            onChange={handleInputChange}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <CustomInputField
                            label="Address"
                            name="CD_Address"
                            value={clientData.CD_Address}
                            onChange={handleInputChange}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <CustomSelectBox
                            label="Country"
                            options={countries}
                            Value={country}
                            setValue={setCountry}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <CustomSelectBox
                            label="State"
                            options={states}
                            Value={state}
                            setValue={setState}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <CustomInputField
                            label="City"
                            name="CD_City"
                            value={clientData.CD_City}
                            onChange={handleInputChange}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <CustomInputField
                            label="Zipcode"
                            name="CD_Zipcode"
                            value={clientData.CD_Zipcode}
                            onChange={handleInputChange}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <CustomInputField
                            label="Point of Contact"
                            name="CD_POC"
                            value={clientData.CD_POC}
                            onChange={handleInputChange}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <CustomInputField
                            label="Mobile Phone"
                            name="CD_Phone"
                            value={clientData.CD_Phone}
                            onChange={handleInputChange}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <CustomInputField
                            label="Email ID"
                            name="CD_Email"
                            value={clientData.CD_Email}
                            onChange={handleInputChange}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <CustomInputField
                            label="Billing Address"
                            name="CD_BillingAddress"
                            value={clientData.CD_BillingAddress}
                            onChange={handleInputChange}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <CustomSelectBox
                            label="Billing Country"
                            options={countries}
                            Value={billingcountry}
                            setValue={setBillingCountry}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <CustomSelectBox
                            label="Billing State"
                            options={states}
                            Value={billingState}
                            setValue={setBillingstate}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <CustomInputField
                            label="Billing City"
                            name="CD_Billing_City"
                            value={clientData.CD_Billing_City}
                            onChange={handleInputChange}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <CustomInputField
                            label="Billing Zipcode"
                            name="CD_Billing_Zipcode"
                            value={clientData.CD_Billing_Zipcode}
                            onChange={handleInputChange}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <CustomSelectBox
                            label="Status"
                            options={statusList}
                            Value={status}
                            setValue={setStatus}
                            fullWidth
                        />
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
};

export default AddClient;
